import { ReactNode, useState } from "react";
import styled from "styled-components";
import {
  BACKGROUND_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { AffiliateState } from "../../interfaces/affiliate";
import { currentAffiliateTier } from "./LifecyclePanel";
import { affiliateNickname } from "./affiliateIdentity";
import {
  AffiliateAdminRun,
  AffiliateNicknameEditor,
} from "./AffiliateNicknameEditor";

const DirectoryToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  h2 {
    flex: 1;
    margin: 0;
  }
  > details {
    margin: 0;
  }
  > details[open] {
    flex: 1 1 100%;
    order: 1;
  }
`;

export function AffiliateDirectory({
  data,
  locked = false,
  run,
  actions,
}: {
  data?: AffiliateState;
  locked?: boolean;
  run?: AffiliateAdminRun;
  actions?: ReactNode;
}) {
  const [search, setSearch] = useState("");
  const [includeEnded, setIncludeEnded] = useState(false);
  const affiliates = (data?.affiliates || []).map((affiliate) => ({
    ...affiliate,
    nickname: affiliateNickname(data, affiliate.address),
    currentTier: currentAffiliateTier(
      affiliate,
      Date.parse(data?.ledger.evaluation_at || data?.server_time || "")
    ),
  }));
  const matches = affiliates.filter(
    (affiliate) =>
      (includeEnded || !affiliate.terminated_at) &&
      `${affiliate.nickname} ${affiliate.address} ${
        affiliate.currentTier || ""
      }`
        .toLowerCase()
        .includes(search.trim().toLowerCase())
  );
  return (
    <section aria-label="Affiliate dashboard directory">
      <DirectoryToolbar>
        <h2>All affiliates</h2>
        {actions}
      </DirectoryToolbar>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <label
          htmlFor="affiliate-directory-search"
          style={{ width: 340, maxWidth: "100%" }}
        >
          Search nickname, wallet or tier
          <input
            id="affiliate-directory-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Nickname, 0x… or VIP2"
          />
        </label>
        <label
          htmlFor="affiliate-directory-ended"
          style={{ flexDirection: "row", alignItems: "center" }}
        >
          <input
            id="affiliate-directory-ended"
            type="checkbox"
            checked={includeEnded}
            onChange={(event) => setIncludeEnded(event.target.checked)}
          />
          Include ended affiliates
        </label>
        <span>
          {data
            ? `${matches.length} ${
                matches.length === 1 ? "affiliate" : "affiliates"
              }`
            : "Loading affiliates…"}
        </span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>Affiliate</th>
              <th>Tier</th>
              <th>Status</th>
              <th>Referral codes</th>
              <th>Traders</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {matches.map((affiliate) => (
              <tr key={affiliate.address.toLowerCase()}>
                <td style={{ minWidth: 260 }}>
                  <strong
                    style={{
                      display: "block",
                      fontWeight: 500,
                      marginBottom: 6,
                    }}
                  >
                    {affiliate.nickname || "Unnamed affiliate"}
                  </strong>
                  <code style={{ color: TEXT_COLORS.two, fontSize: 11 }}>
                    {affiliate.address}
                  </code>
                </td>
                <td>{affiliate.currentTier || "Awaiting backend time"}</td>
                <td>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "5px 9px",
                      borderRadius: 5,
                      background: affiliate.terminated_at
                        ? BACKGROUND_COLORS.eight
                        : COLORS.green.six,
                      color: affiliate.terminated_at
                        ? TEXT_COLORS.two
                        : COLORS.green.one,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span aria-hidden="true">●</span>
                    {affiliate.terminated_at ? "Ended" : "Active"}
                  </span>
                </td>
                <td>
                  {
                    data?.codes.filter(
                      (code) =>
                        code.affiliate.toLowerCase() ===
                        affiliate.address.toLowerCase()
                    ).length
                  }
                </td>
                <td>
                  {
                    data?.attachments.filter(
                      (trader) =>
                        trader.affiliate.toLowerCase() ===
                        affiliate.address.toLowerCase()
                    ).length
                  }
                </td>
                <td>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <a
                      href={`/affiliates?affiliate=${encodeURIComponent(
                        affiliate.address
                      )}`}
                      aria-label={`View dashboard for ${affiliate.address}`}
                    >
                      View dashboard →
                    </a>
                    <AffiliateNicknameEditor
                      address={affiliate.address}
                      nickname={affiliate.nickname}
                      disabled={
                        locked ||
                        data?.capabilities?.set_affiliate_nickname !== true
                      }
                      run={run}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !matches.length && (
          <p>
            {affiliates.length
              ? "No affiliates match these filters."
              : "No approved affiliates yet."}
          </p>
        )}
      </div>
    </section>
  );
}
